import { Star } from "lucide-react";
import { Label } from "@/components/ui/label";

interface HalfStarRatingProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  required?: boolean;
}

export function HalfStarRating({ label, value, onChange, required }: HalfStarRatingProps) {
  const stars = [1, 2, 3, 4, 5];

  const handleClick = (starIndex: number, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftHalf = x < rect.width / 2;
    const newValue = isLeftHalf ? starIndex - 0.5 : starIndex;
    // Toggle off if clicking same value
    onChange(newValue === value ? 0 : newValue);
  };

  return (
    <div>
      <Label className="mb-1 block text-sm">
        {label}
        {required && " *"}
      </Label>
      <div className="flex gap-0.5">
        {stars.map((n) => {
          const filled = value >= n;
          const halfFilled = !filled && value >= n - 0.5;

          return (
            <button
              key={n}
              type="button"
              onClick={(e) => handleClick(n, e)}
              className="relative p-0.5 cursor-pointer"
              aria-label={`${n} estrellas`}
            >
              {/* Background (empty) star */}
              <Star className="h-7 w-7 text-muted-foreground/25" />

              {/* Filled overlay */}
              {(filled || halfFilled) && (
                <div
                  className="absolute inset-0 overflow-hidden p-0.5"
                  style={{ width: halfFilled ? "50%" : "100%" }}
                >
                  <Star className="h-7 w-7 fill-warning text-warning" />
                </div>
              )}
            </button>
          );
        })}
        {value > 0 && (
          <span className="ml-1.5 self-center text-sm font-medium text-muted-foreground">
            {value.toFixed(1)}
          </span>
        )}
      </div>
    </div>
  );
}
